import { useEffect, useMemo, useState } from 'react';
import { getSupabaseClient } from '../utils/supabaseClient';

/**
 * Generic table fetcher with optional mapper and fallback.
 * @param {object} params
 * @param {string} params.table - Supabase table name
 * @param {function} [params.mapRow] - maps each row into the component's expected shape
 * @param {object} [params.query] - optional query config: { select, orderBy: { column, ascending }, eq: { col: val }, limit }
 * @param {function} [params.getFallback] - returns fallback data when Supabase is not configured or empty
 */
export function useSupabaseData({ table, mapRow, query, getFallback }) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const supabase = useMemo(() => getSupabaseClient(), []);

	useEffect(() => {
		let isMounted = true;
		async function run() {
			setLoading(true);
			setError(null);
			try {
				if (!supabase) {
					const fb = getFallback ? getFallback() : null;
					if (isMounted) setData(fb);
					return;
				}
				let sel = supabase.from(table).select(query?.select || '*');
				if (query?.eq) {
					Object.entries(query.eq).forEach(([col, val]) => {
						sel = sel.eq(col, val);
					});
				}
				if (query?.orderBy?.column) {
					sel = sel.order(query.orderBy.column, { ascending: query.orderBy.ascending !== false });
				}
				if (query?.limit) {
					sel = sel.limit(query.limit);
				}
				const { data: rows, error: err } = await sel;
				if (err) throw err;
				const mapped = Array.isArray(rows) ? (mapRow ? rows.map(mapRow) : rows) : rows;
				if (isMounted) setData(mapped);
			} catch (e) {
				// Fallback on error
				const fb = getFallback ? getFallback() : null;
				if (isMounted) {
					setError(e);
					setData(fb);
				}
			} finally {
				if (isMounted) setLoading(false);
			}
		}
		run();
		return () => { isMounted = false; };
	}, [supabase, table, JSON.stringify(query)]);

	return { data, loading, error };
}


